import React from "react";
import { Link } from "react-router-dom";
import { HERO } from "@/constants/testIds";
import { useContent } from "@/context/ContentContext";
import { Flame, Sparkles, Clock4, Utensils, ArrowRight, MapPin } from "lucide-react";

const ICONS = { Flame, Sparkles, Clock4, Utensils };

export default function Home() {
    const c = useContent();
    const h = c.home || {};
    const g = c.global || {};
    const showHalaalHero = g.show_halaal_hero !== false;

    return (
        <div>
            {/* HERO */}
            <section className="relative min-h-[85vh] flex items-center overflow-hidden">
                <img src={h.hero_image_url} alt="Signature dish" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/85 to-brand-ink/40" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-ink to-transparent" />
                <div className="container-wide relative z-10 py-24">
                    <img src={g.logo_url} alt="Umami Kraft" className="h-24 md:h-32 w-auto object-contain mb-6 [filter:invert(1)_hue-rotate(180deg)_saturate(1.4)]" />
                    <h1 className="mt-2 font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-brand-cream max-w-3xl leading-[1.05]">
                        {h.hero_headline}
                    </h1>
                    <p className="mt-6 max-w-xl text-base md:text-lg text-brand-cream/80 leading-relaxed">
                        {h.hero_subheading}
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4">
                        <Link data-testid={HERO.orderBtn} to="/order" className="btn-primary">
                            {h.hero_cta_primary || "Order Now"} <ArrowRight size={16} className="ml-2" />
                        </Link>
                        <Link data-testid={HERO.menuBtn} to="/order" className="btn-outline">
                            {h.hero_cta_secondary || "View Menu"}
                        </Link>
                    </div>
                    <div className="mt-10 flex items-center gap-2 text-sm text-brand-cream/70">
                        <MapPin size={14} className="text-brand-olive" />
                        <span>{h.hero_pickup_text}</span>
                    </div>
                    {showHalaalHero && (
                        <div className="mt-8 flex items-center gap-3">
                            <img src={g.halaal_logo_url} alt="Halaal Certified" className="h-16 w-auto object-contain" />
                            <div>
                                <p className="text-xs tracking-[0.2em] uppercase text-brand-gold">{g.halaal_caption}</p>
                                <p className="text-xs text-brand-cream/60">{g.halaal_subcaption}</p>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* VALUE STRIP */}
            <section className="border-y border-brand-line bg-brand-charcoal">
                <div className="container-wide grid grid-cols-2 md:grid-cols-4 gap-6 py-10">
                    {(h.value_strip || []).map((v) => {
                        const Icon = ICONS[v.icon] || Utensils;
                        return (
                            <div key={v.label} className="flex flex-col items-start gap-3">
                                <Icon size={22} className="text-brand-olive" />
                                <p className="text-sm text-brand-cream/90 leading-tight">{v.label}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* FEATURED CATEGORIES */}
            <section className="container-wide py-24">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <p className="font-script text-brand-gold text-3xl">{h.categories_script}</p>
                        <h2 className="font-display text-3xl sm:text-4xl mt-1">{h.categories_title}</h2>
                    </div>
                    <Link to="/order" className="hidden md:inline text-sm text-brand-gold hover:underline">
                        Full menu →
                    </Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                    {(h.category_tiles || []).map((cat, i) => (
                        <Link
                            key={cat.slug}
                            to={`/order?category=${cat.slug}`}
                            className={`group relative overflow-hidden border border-brand-line hover:border-brand-olive transition-all ${
                                i === 0 ? "md:col-span-2 md:row-span-2 aspect-[4/3]" : "aspect-square"
                            }`}
                        >
                            <img src={cat.img_url} alt={cat.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                            <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/95 via-brand-ink/30 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-5">
                                <p className="font-display text-xl md:text-2xl text-brand-cream">{cat.name}</p>
                                <p className="text-xs text-brand-gold mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    Explore →
                                </p>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* DAILY SPECIALS BANNER */}
            <section className="relative overflow-hidden">
                <div className="container-wide py-20">
                    <div className="grid md:grid-cols-2 gap-10 items-center border border-brand-line bg-brand-charcoal p-10 md:p-16 rounded-sm">
                        <div>
                            <p className="font-script text-brand-gold text-3xl">{h.specials_script}</p>
                            <h2 className="font-display text-3xl md:text-5xl mt-2">{h.specials_title}</h2>
                            <p className="mt-4 text-brand-mute leading-relaxed">
                                {h.specials_body}
                            </p>
                            <Link
                                data-testid={HERO.dailySpecialsBtn}
                                to="/specials"
                                className="btn-outline mt-8"
                            >
                                {h.specials_cta || "See today's specials"}
                            </Link>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {(h.specials_rotation_images || []).map((src, i) => (
                                <div key={i} className="aspect-square border border-brand-line overflow-hidden">
                                    <img src={src} alt={`Daily special ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CONTACT SNIPPET */}
            <section className="container-wide py-20 border-t border-brand-line">
                <div className="grid md:grid-cols-2 gap-12">
                    <div>
                        <p className="font-script text-brand-gold text-3xl">{h.contact_script}</p>
                        <h2 className="font-display text-3xl md:text-4xl mt-1">{h.contact_title}</h2>
                        <p className="mt-4 text-brand-mute">
                            {g.address_line1}, {g.address_line2}
                        </p>
                        <p className="mt-2 text-brand-cream/80">{(g.hours_lines || []).join(" · ")}</p>
                        <p className="mt-2 text-brand-cream/80">
                            <a href={`tel:${g.phone_tel}`} className="hover:text-brand-gold">{g.phone_display}</a>
                        </p>
                    </div>
                    <div className="aspect-video border border-brand-line overflow-hidden">
                        <iframe
                            title="Umami Kraft Map"
                            width="100%"
                            height="100%"
                            style={{ border: 0, filter: "grayscale(0.4) contrast(1.1)" }}
                            loading="lazy"
                            src={`https://www.google.com/maps?q=${encodeURIComponent(`${g.address_line1}, ${g.address_line2}`)}&output=embed`}
                        />
                    </div>
                </div>
            </section>
        </div>
    );
}
